import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

import {
  ACCENT_COLOR_OPTIONS,
  CUSTOM_ACCENT_COLOR,
  type AccentColor,
  type AccentColorOption,
  getPresetAccentTokens,
  isPresetAccentColor,
} from '@/providers/theme/theme-accent.constants';
import { getCustomAccentTokens, normalizeHexColor } from '@/providers/theme/theme-accent.utils';
import {
  type ResolvedTheme,
  type ThemeMode,
  readStoredAccentColor,
  readStoredCustomAccentColor,
  readStoredTheme,
  resolveTheme,
} from '@/providers/theme/theme-provider.utils';

interface ThemeContextType {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  resolvedTheme: ResolvedTheme;
  accentColor: AccentColor;
  customAccentColor: string;
  accentOptions: readonly AccentColorOption[];
  setAccentColor: (accentColor: AccentColor) => void;
  setCustomAccentColor: (hexColor: string) => boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeMode;
  storageKey?: string;
  accentStorageKey?: string;
  customAccentStorageKey?: string;
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'trafficpulse-theme',
  accentStorageKey = 'trafficpulse-accent-color',
  customAccentStorageKey = 'trafficpulse-custom-accent-color',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>(() =>
    typeof window === 'undefined' ? defaultTheme : readStoredTheme(storageKey, defaultTheme),
  );

  const [accentColor, setAccentColorState] = useState<AccentColor>(() => {
    return typeof window === 'undefined' ? 'blue' : readStoredAccentColor(accentStorageKey);
  });
  const [customAccentColor, setCustomAccentColorState] = useState<string>(() => {
    return typeof window === 'undefined' ? '#2563eb' : readStoredCustomAccentColor(customAccentStorageKey);
  });
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    const effectiveTheme = resolveTheme(theme);
    root.classList.add(effectiveTheme);
    setResolvedTheme(effectiveTheme);
  }, [theme]);

  useEffect(() => {
    const root = window.document.documentElement;
    const tokens = isPresetAccentColor(accentColor)
      ? getPresetAccentTokens(accentColor, resolvedTheme)
      : getCustomAccentTokens(customAccentColor);

    root.style.setProperty('--primary', tokens.primary);
    root.style.setProperty('--primary-foreground', tokens.primaryForeground);
    root.style.setProperty('--ring', tokens.ring);
  }, [accentColor, customAccentColor, resolvedTheme]);

  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      const newTheme = e.matches ? 'dark' : 'light';
      root.classList.add(newTheme);
      setResolvedTheme(newTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = (newTheme: ThemeMode) => {
    localStorage.setItem(storageKey, newTheme);
    setThemeState(newTheme);
  };

  const setAccentColor = (newAccentColor: AccentColor) => {
    localStorage.setItem(accentStorageKey, newAccentColor);
    setAccentColorState(newAccentColor);
  };

  const setCustomAccentColor = (hexColor: string) => {
    const normalized = normalizeHexColor(hexColor);
    if (!normalized) return false;
    localStorage.setItem(customAccentStorageKey, normalized);
    localStorage.setItem(accentStorageKey, CUSTOM_ACCENT_COLOR);
    setCustomAccentColorState(normalized);
    setAccentColorState(CUSTOM_ACCENT_COLOR);
    return true;
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        resolvedTheme,
        accentColor,
        customAccentColor,
        accentOptions: ACCENT_COLOR_OPTIONS,
        setAccentColor,
        setCustomAccentColor,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
